import { useParams, Link } from 'react-router-dom';

const sections = {
  privacy: {
    title: 'Privacy Policy',
    content: `Last Updated: January 2025

Safe Florida ("we," "our," "us") is committed to protecting the privacy of our members and visitors. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.

INFORMATION WE COLLECT
We collect personal information you voluntarily provide, including: name, email address, phone number, mailing address, payment information (processed securely through PayPal), and membership preferences.

We automatically collect certain information when you visit our website, including IP address, browser type, operating system, access times, and pages viewed.

HOW WE USE YOUR INFORMATION
We use your information to: process membership applications and payments, send you updates about Safe Florida initiatives, manage your member portal account, improve our website and services, comply with legal obligations, and communicate about community events and programs.

INFORMATION SHARING
We do not sell, trade, or rent your personal information to third parties. We may share information with: service providers who assist our operations (e.g., PayPal for payment processing), legal authorities when required by law, and affiliated organizations with your consent.

DATA SECURITY
We implement industry-standard security measures to protect your personal information. However, no method of electronic transmission or storage is 100% secure.

YOUR RIGHTS
You may access, update, or delete your personal information through your member portal or by contacting us. You may opt out of marketing communications at any time.

CONTACT US
For privacy-related inquiries, contact us at privacy@safeflorida.org or through our Contact page.`
  },
  terms: {
    title: 'Terms of Service',
    content: `Last Updated: January 2025

Welcome to Safe Florida. By accessing and using our website and services, you agree to be bound by these Terms of Service.

ACCEPTANCE OF TERMS
By using our website, you acknowledge that you have read, understood, and agree to these terms. If you do not agree, please do not use our services.

MEMBERSHIP
Safe Florida membership is available to individuals who complete the registration process and pay the monthly membership fee of $250. Membership benefits are subject to change with reasonable notice. Members must comply with our community guidelines and Code of Conduct.

PAYMENTS
All payments are processed securely through PayPal. Membership fees are billed monthly. Refunds may be issued at our discretion within 30 days of payment. You are responsible for keeping your payment information current.

STORE PURCHASES
All merchandise sales are final unless items are defective or damaged upon receipt. Returns must be initiated within 14 days of delivery. Shipping times and costs vary by location.

INTELLECTUAL PROPERTY
All content on this website, including text, graphics, logos, and the Safe Florida brand, is our property and is protected by intellectual property laws. You may not reproduce, distribute, or use our content without written permission.

USER CONDUCT
You agree not to: use the website for any unlawful purpose, impersonate any person or entity, interfere with the website's operation, or share misleading information.

LIMITATION OF LIABILITY
Safe Florida is not liable for any indirect, incidental, or consequential damages arising from your use of our website or services.

GOVERNING LAW
These terms are governed by the laws of the State of Florida. Any disputes shall be resolved in the courts of St. Lucie County, Florida.

CONTACT
For questions about these terms, contact us at legal@safeflorida.org.`
  },
  'membership-agreement': {
    title: 'Membership Agreement',
    content: `Last Updated: January 2025

SAFE FLORIDA MEMBERSHIP AGREEMENT

This Membership Agreement ("Agreement") is entered into between Safe Florida, a 501(c)(4) social welfare organization ("Organization"), and you ("Member").

MEMBERSHIP TERMS
1. Membership Fee: $250 per month, payable via PayPal.
2. Membership is month-to-month and may be cancelled at any time with 30 days written notice.
3. Membership benefits begin upon receipt of the first payment.

MEMBER BENEFITS
As a member, you are entitled to:
- Access to the Safe Florida Member Portal
- Early access to critical community decisions
- Inside information on development plans
- Participation in community voting and initiatives
- Access to exclusive documents and resources
- Attendance at member-only events
- Support from the Legal Defense Fund
- Priority Zone updates and reports

MEMBER RESPONSIBILITIES
Members agree to:
- Pay monthly dues on time
- Participate in good faith in community initiatives
- Maintain confidentiality of member-only information
- Conduct themselves professionally at all events
- Comply with all applicable laws and regulations

USE OF FUNDS
Member dues are used to fund: legal defense actions, community organizing, land preservation efforts, administrative operations, and communication and outreach programs.

TERMINATION
Either party may terminate membership with 30 days written notice. Safe Florida reserves the right to revoke membership for violation of this Agreement or Code of Conduct.

501(c)(4) STATUS
Safe Florida is organized as a 501(c)(4) social welfare organization. Membership dues are not tax-deductible as charitable contributions.

GOVERNING LAW
This Agreement is governed by the laws of the State of Florida.

By becoming a member, you acknowledge that you have read, understood, and agree to this Membership Agreement.

Safe Florida
Port St. Lucie, Florida

Chuck Clark, Founder & Leader`
  },
  'refund-policy': {
    title: 'Refund Policy',
    content: `Last Updated: January 2025

SAFE FLORIDA REFUND POLICY

MEMBERSHIP REFUNDS
Safe Florida membership operates on a month-to-month basis. Members may cancel at any time with 30 days written notice.

Refund Eligibility:
- Full refund available within 30 days of initial payment if membership has not been actively used
- Prorated refunds may be issued for cancellations within the first 14 days of a billing cycle
- No refunds for partial months after the 14-day window
- Refund requests must be submitted in writing via email to billing@safeflorida.org

Processing:
- Refunds are processed through the original payment method (PayPal)
- Allow 5-10 business days for refund processing
- Refund confirmation will be sent via email

STORE PURCHASE REFUNDS
Merchandise returns are accepted within 14 days of delivery.

Conditions:
- Items must be in original, unworn, unwashed condition with tags attached
- Defective or damaged items will be replaced or refunded at no additional cost
- Custom or personalized items are non-refundable
- Shipping costs for returns are the responsibility of the buyer unless item is defective
- Yard signs and stickers are non-refundable once opened

Process:
- Contact store@safeflorida.org with your order number and reason for return
- You will receive a return authorization and shipping instructions
- Refunds are processed within 5-10 business days of receiving the returned item

DISPUTES
If you believe you have been charged in error, contact billing@safeflorida.org within 60 days of the charge. We will investigate and respond within 5 business days.

CONTACT
For refund inquiries: billing@safeflorida.org
Safe Florida, Port St. Lucie, FL`
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    content: `Last Updated: January 2025

SAFE FLORIDA COOKIE POLICY

WHAT ARE COOKIES
Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and enabling essential website functionality.

COOKIES WE USE

Essential Cookies (Required):
- Authentication cookies to keep you logged into your member portal
- Session cookies to maintain your shopping cart
- Security cookies to protect against unauthorized access

Functional Cookies:
- Preference cookies to remember your language and display settings
- Form cookies to save your contact form progress

Analytics Cookies:
- We may use analytics services to understand how visitors use our website
- These cookies collect anonymous data about page visits and user behavior
- This data helps us improve our website and services

THIRD-PARTY COOKIES
Our website integrates with third-party services that may set their own cookies:
- PayPal: Payment processing and security
- Social media platforms: Share buttons and embedded content

HOW TO MANAGE COOKIES
You can control cookies through your browser settings:
- Most browsers allow you to block or delete cookies
- Blocking essential cookies may affect website functionality
- You can set your browser to notify you when cookies are being set

CONSENT
By using our website, you consent to our use of essential cookies. You may opt out of non-essential cookies through your browser settings.

CHANGES TO THIS POLICY
We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated revision date.

CONTACT
For questions about our Cookie Policy: privacy@safeflorida.org`
  },
  'disclaimer': {
    title: 'Disclaimer',
    content: `Last Updated: January 2025

SAFE FLORIDA DISCLAIMER

GENERAL DISCLAIMER
The information provided on the Safe Florida website and through our services is for general informational purposes only. While we strive to keep information current and accurate, we make no representations or warranties of any kind about the completeness, accuracy, reliability, suitability, or availability of the information, products, services, or related graphics contained on the website.

NOT LEGAL ADVICE
Nothing on this website constitutes legal advice. The Legal Defense Fund information describes our advocacy and legal support programs but should not be construed as attorney-client relationship establishment. Consult a qualified attorney for specific legal questions.

NOT FINANCIAL ADVICE
Membership fees and financial information presented on this website are for organizational purposes only. Safe Florida does not provide financial, tax, or investment advice. Consult a qualified financial professional for personal financial decisions.

501(c)(4) STATUS
Safe Florida operates as a 501(c)(4) social welfare organization. Membership dues and contributions to Safe Florida are NOT tax-deductible as charitable contributions for federal income tax purposes. Consult your tax advisor regarding deductibility.

EXTERNAL LINKS
Our website may contain links to external websites. We have no control over the content and nature of these sites and are not responsible for their content or privacy practices.

LIMITATION OF LIABILITY
In no event shall Safe Florida, its officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our website or services.

ENDORSEMENTS
Safe Florida does not endorse any specific political candidate or party. As a 501(c)(4) organization, our advocacy work focuses on policy issues, community welfare, and environmental protection.

ACCURACY OF INFORMATION
While we make reasonable efforts to ensure information accuracy, we cannot guarantee that all information on our website is complete, accurate, or current. Priority Zone data, development information, and legal updates are subject to change.

USER RESPONSIBILITY
Users are responsible for independently verifying information before making decisions based on content found on this website.

GOVERNING LAW
This disclaimer is governed by the laws of the State of Florida.

CONTACT
For questions or concerns: legal@safeflorida.org
Safe Florida, Port St. Lucie, FL`
  }
};

const tabs = [
  { key: 'privacy', label: 'Privacy Policy' },
  { key: 'terms', label: 'Terms of Service' },
  { key: 'membership-agreement', label: 'Membership Agreement' },
  { key: 'refund-policy', label: 'Refund Policy' },
  { key: 'cookie-policy', label: 'Cookie Policy' },
  { key: 'disclaimer', label: 'Disclaimer' },
];

export default function Legal() {
  const { section } = useParams();
  const current = section || 'privacy';
  const data = sections[current] || sections.privacy;

  return (
    <div className="pt-16" data-testid="legal-page">
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">{data.title}</h1>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200 pb-4">
            {tabs.map(t => (
              <Link key={t.key} to={`/legal/${t.key}`}
                className={`px-4 py-2 text-sm font-body rounded-md transition-colors ${current === t.key ? 'bg-navy text-white' : 'text-gray-600 hover:bg-navy/5'}`}
                data-testid={`legal-tab-${t.key}`}>
                {t.label}
              </Link>
            ))}
          </div>

          {/* Content */}
          <div className="prose prose-sm max-w-none font-body text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="legal-content">
            {data.content}
          </div>
        </div>
      </section>
    </div>
  );
}
